import { IconButton, ListItem, Stack, TextField } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { EllipsisOMIcon, PlusOMIcon } from '@omnea-app/icons'
import { AutocompleteActionsPaper, BaseAutocomplete } from '@omnea-app/omui/BaseAutocomplete'
import { PauseReasonDialog, type PauseReasonDialogTarget } from './PauseReasonDialog'
import { useGetPauseReasons } from './useGetPauseReasons'
import type { AutocompleteRenderInputParams, PaperProps } from '@mui/material'

type PauseReasonOption = { id: string; label: string }

type Props = {
  value: string | null
  onChange: (reasonId: string | null) => void
  label: string
  required?: boolean
  error?: boolean
  helperText?: string
}

/** Reason picker for the pause modal. Owns the org's reason list and the dialog for adding,
 * renaming and removing entries in it. */
export const PauseReasonSelect = ({ value, onChange, label, required, error, helperText }: Props) => {
  const { t } = useTranslation('requests', { keyPrefix: 'pause' })
  const { user } = useAuthenticatedUser()
  const { data: pauseReasons, isLoading } = useGetPauseReasons()
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [dialogTarget, setDialogTarget] = useState<PauseReasonDialogTarget | null>(null)

  const options = useMemo<PauseReasonOption[]>(
    () => (pauseReasons ?? []).map(({ id, text }) => ({ id, label: text })),
    [pauseReasons]
  )
  const selected = options.find((option) => option.id === value) ?? null

  // A reason removed in another session drops out of the refetched list, which would leave the
  // field looking empty while the form still held its id and submitted a pause the API rejects.
  useEffect(() => {
    if (pauseReasons && value !== null && !pauseReasons.some(({ id }) => id === value)) {
      onChange(null)
    }
  }, [pauseReasons, value, onChange])

  const openDialog = useCallback((target: PauseReasonDialogTarget) => {
    setIsOpen(false)
    setDialogTarget(target)
  }, [])

  // Autocomplete mirrors the selected option's label into its input, so only text the user has
  // actually typed should seed the add dialog.
  const typedFilterText = inputValue === selected?.label ? '' : inputValue

  // Held in a ref rather than closed over, so the paper below keeps one identity. MUI treats that
  // slot as an element type, so a new function per keystroke remounts the popup and every option.
  const typedFilterTextRef = useRef(typedFilterText)
  typedFilterTextRef.current = typedFilterText

  const addNewPaper = useCallback(
    (paperProps: PaperProps) => (
      <AutocompleteActionsPaper
        actions={[
          {
            label: t('add-reason'),
            icon: PlusOMIcon,
            onSelect: () => openDialog({ reasonId: null, text: typedFilterTextRef.current }),
          },
        ]}
        {...paperProps}
      />
    ),
    [openDialog, t]
  )

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      label={label}
      required={required}
      placeholder={t('reason-placeholder')}
      error={error}
      helperText={helperText}
    />
  )

  return (
    <>
      <BaseAutocomplete<PauseReasonOption>
        options={options}
        value={selected}
        loading={isLoading}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selectedOption) => option.id === selectedOption.id}
        onChange={(_, newValue) => onChange(newValue?.id ?? null)}
        renderInput={renderInput}
        renderOption={(optionProps, option) => (
          <ListItem
            {...optionProps}
            key={option.id}
            sx={{
              '& .manage-reason-button': { opacity: 0 },
              '&:hover .manage-reason-button': { opacity: 1 },
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
              {option.label}
              {user.isNonViewerAdmin && (
                <IconButton
                  className="manage-reason-button"
                  size="small"
                  aria-label={t('manage-reason', { reason: option.label })}
                  // The row is the option's own click target, so the press must not reach it. It also
                  // must not move focus: the popup closes on blur, and an option unmounted between
                  // mousedown and mouseup never fires a click at all.
                  onMouseDown={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  onClick={(event) => {
                    event.stopPropagation()
                    openDialog({ reasonId: option.id, text: option.label })
                  }}
                >
                  <EllipsisOMIcon />
                </IconButton>
              )}
            </Stack>
          </ListItem>
        )}
        slots={{ paper: addNewPaper }}
      />
      {dialogTarget !== null && (
        <PauseReasonDialog
          target={dialogTarget}
          onClose={() => setDialogTarget(null)}
          onAdded={onChange}
          onRemoved={(reasonId) => {
            if (reasonId === value) onChange(null)
          }}
        />
      )}
    </>
  )
}
